/**
 * This is a simple base page for the card list page.
 *
 * Most of the pages will render a list of cards, so this is a simple base page
 */

import { Button, Switch, Title } from 'components';
import { LinkGuard } from 'components/RoleGuard';
import { cn } from 'domain/core';
import { useFormData } from 'domain/hooks';
import { getEditLink, RelsTypes } from 'domain/hooks/api/api';
import {
	INITIAL_PAGINATION,
	useApiList,
	useColumnHeaders,
} from 'domain/hooks/useApiList';
import { useDocumentTitle } from 'domain/hooks/useDocumentTitle';
import { Layout } from 'domain/models';
import { atStartOfDay } from 'domain/utils/date.utils';
import { BaseQueryParams } from 'domain/utils/query.utils';
import { DatePickerComponent } from 'pages/schemas/widgets/DatePicker';
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { EmptyCard } from './EmptyCard';
import { ErrorCard } from './ErrorCard';

export type CardListPageProps = {
	rel: RelsTypes;
};

export const CardListContext = createContext({
	expanded: -1,
	setExpanded: undefined as any,
	cardLayout: Layout.Card,
	rel: 'dashboards' as RelsTypes,
	pagination: INITIAL_PAGINATION,
	setPagination: undefined as any,
});

export const CARD_ITEM_CLASSNAME = 'list-element-';

export const CardListPage = ({ rel }: CardListPageProps) => {
	const { t } = useTranslation(['entities', 'labels']);

	const [dateForm, setDateForm] = useFormData({
		date: atStartOfDay(new Date()),
		pagination: INITIAL_PAGINATION,
		deleted: false,
	});

	const pageQuery = useMemo(() => {
		return new BaseQueryParams(
			{ key: 'date', value: dateForm.date },
			{ key: 'deleted', value: dateForm.deleted },
			{ key: 'page', value: dateForm.pagination.number - 1 },
			{ key: 'size', value: dateForm.pagination.size }
		);
	}, [dateForm]);

	const { list, side, cardLayout, page, error, loading } = useApiList(
		rel,
		pageQuery
	);

	const [queryParams, setQueryParams] = useSearchParams({});
	useDocumentTitle(rel);

	const title = rel;

	const setExpanded = useCallback(
		(id: string | number) => {
			if (parseInt(id as string) < 0) {
				return;
			}
			if (id === expanded) {
				return;
			}
			if (!id) {
				setQueryParams('expand=false');
			}
			setQueryParams('id=' + id + '&expand=true');
		},
		[setQueryParams]
	);

	const layout = useMemo(() => {
		// Extract from hash
		const expand = queryParams.get('expand');
		if (expand) {
			return Layout.Side;
		}
		return cardLayout;
	}, [cardLayout, queryParams]);

	const expanded = useMemo(() => {
		// Extract from hash
		const id = queryParams.get('id');
		if (id) {
			return parseInt(id as string);
		}
		return -1;
	}, [queryParams]);

	// Scroll effect when expanded
	useEffect(() => {
		if (expanded !== -1) {
			// Find the element with className CARD_ITEM_CLASSNAME + expanded
			const element = document.querySelector(
				'.' + CARD_ITEM_CLASSNAME + expanded
			) as HTMLElement;

			if (element === null) {
				return;
			}

			const topPos = element.offsetTop;

			const parent = document.querySelector(
				'.list-element-wrapper'
			) as HTMLElement;
			if (!parent) {
				return;
			}

			// Get the parent offsetTop, and substract it from the element offsetTop
			const parentTop = parent.offsetTop;

			parent.scrollTo({
				top: topPos - parentTop,
			});
		}
	}, [expanded]);

	const range = false; // FIXME
	const showGroupButtons = true; // FIXME: This should be by rel, not by type

	return (
		<CardListContext.Provider
			value={{
				expanded,
				setExpanded,
				cardLayout: layout,
				rel,
				pagination: page,
				setPagination: setDateForm('pagination'),
			}}>
			<div className='flex w-full flex-1 flex-col justify-center px-4 pt-6 md:px-12'>
				<div className='flex w-full content-start items-start justify-between py-4	pr-4'>
					<div className='flex flex-col'>
						<Title>{t(`entities:${title}`)}</Title>
						{range && (
							<div className='h-6'>
								<DatePickerComponent
									variant='small'
									className='m-0 h-6 w-32'
									value={dateForm.date}
									onChange={(e) => setDateForm('date')(atStartOfDay(e))}
								/>
							</div>
						)}
					</div>
					{showGroupButtons && (
						<div className='align-center flex items-center'>
							<Switch
								label={t('labels:show_deleted') as string}
								checked={dateForm.deleted}
								onChange={() => setDateForm('deleted')(!dateForm.deleted)}
								className='mr-4'
							/>
							<LinkGuard role='ROLE_ADMIN' to={getEditLink(rel)} hidden>
								<Button size='sm'>{t('labels:new')}</Button>
							</LinkGuard>
						</div>
					)}
				</div>
				<CardListLayout list={list} side={side} />
				{list.length === 0 && !loading && !error && (
					<EmptyCard key='empty-card' />
				)}
				{error && !loading && <ErrorCard key='empty-card' />}
			</div>
		</CardListContext.Provider>
	);
};

const CardListLayout = ({
	list,
	side,
}: {
	list: ReactNode[];
	side?: ReactNode;
}) => {
	const { t } = useTranslation(['labels']);
	const { cardLayout, rel, pagination, setPagination } =
		useContext(CardListContext);
	const columnHeaders: unknown[] = useColumnHeaders<unknown>(rel);

	switch (cardLayout) {
		case Layout.Card:
			return (
				<div className='flex w-full flex-row'>
					<div className='flex w-full flex-row flex-wrap justify-start'>
						{list.map((card, index) => (
							<div
								key={`card-${index}`}
								className={cn(
									'w-full',
									'pr-4',
									'pb-4',
									'lg:w-1/2',
									'xl:w-1/3'
								)}>
								{card}
							</div>
						))}
					</div>
				</div>
			);

		case Layout.Table:
			if (!list || list.length === 0) {
				return <></>;
			}
			{
				/* Create a responsive table */
			}
			return (
				<div className='flex w-full flex-col justify-start text-sm'>
					<div className='flex w-full flex-row justify-between rounded-sm bg-primary-600 p-2 font-medium text-white'>
						{columnHeaders.map((header: any, index) => (
							<div className='w-full' key={`header-${header}`}>
								{t(`labels:${header}`)}
							</div>
						))}
					</div>
					{list.map((card, index) => (
						<div
							key={`card-${JSON.stringify(card)}`}
							className={cn('w-full', 'p-2', 'hover:bg-gray-300')}>
							{card}
						</div>
					))}
				</div>
			);

		case Layout.Side:
			return (
				<div className='position-relative flex h-[calc(100vh_-_128px)] w-full w-full flex-row flex-wrap justify-start overflow-hidden'>
					<div className='h-[100%] w-full bg-white md:w-1/2	lg:w-1/2'>
						{side}
					</div>
					<div className='list-element-wrapper h-[100%] w-full overflow-y-auto pb-4 pl-4 md:w-1/2 lg:w-1/2'>
						{list.map((card, index) => (
							<div
								key={`card-${JSON.stringify(card)}`}
								className={cn(
									'w-full',
									'pr-4',
									'pb-4',
									CARD_ITEM_CLASSNAME + index
								)}>
								{card}
							</div>
						))}
					</div>
				</div>
			);

		default:
			return <></>;
	}
};

export const CardListRow = ({
	items,
	layout,
}: {
	items: string[];
	layout: number[];
}) => {
	return (
		<div className='flex w-full flex-row justify-between	p-2'>
			{items.map((text: string, index: number) => (
				<div key={`card-item-${text}`}>{text}</div>
			))}
		</div>
	);
};
